import React, { useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import { FaArrowRight, FaUsers, FaHandshake, FaLightbulb, FaCheckCircle } from "react-icons/fa";
import techTraining1 from "../../assets/gallery/education/tech-training1.jpg";
import techTraining2 from "../../assets/gallery/education/tech-training2.jpg";
import healthOutreach1 from "../../assets/gallery/health/health-outreach1.jpeg";
import roadConstruction1 from "../../assets/gallery/community-projects/road-construction1.jpeg";
import roadConstruction2 from "../../assets/gallery/community-projects/road-construction2.jpeg";
import scholarship from "../../assets/gallery/education/scholarship.jpeg";
import health from "../../assets/gallery/health/health.jpeg";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function OurApproach() {
  const approaches = [
    {
      icon: <FaUsers className="text-3xl text-primary-600" />,
      title: "Community-Centered",
      description: "We put our community members at the heart of everything we do. Every program and initiative is designed with their needs, aspirations, and well-being in mind.",
      image: techTraining1
    },
    {
      icon: <FaHandshake className="text-3xl text-primary-600" />,
      title: "Collaborative Partnerships",
      description: "We believe in the power of partnerships. By working together with members, local organizations, and stakeholders, we amplify our impact and reach.",
      image: health
    },
    {
      icon: <FaLightbulb className="text-3xl text-primary-600" />,
      title: "Innovation & Excellence",
      description: "We continuously innovate our programs and services, leveraging technology and best practices to deliver exceptional value to our community.",
      image: roadConstruction2
    }
  ];

  const principles = [
    {
      title: "Transparency",
      description: "We maintain open communication and clear reporting on all our activities, finances, and impact metrics."
    },
    {
      title: "Accountability",
      description: "We hold ourselves accountable to our members, donors, and the communities we serve, ensuring responsible stewardship of resources."
    },
    {
      title: "Inclusivity",
      description: "We welcome all Ojotorians regardless of background, creating an inclusive environment where everyone can thrive."
    },
    {
      title: "Sustainability",
      description: "We design programs for long-term impact, ensuring our initiatives create lasting positive change for generations to come."
    }
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Our Approach"
        subtitle="How we work to create meaningful impact in our community and hometown through strategic, collaborative, and sustainable initiatives."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        heroImage={scholarship}
      />

      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
                How We Make a Difference
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our approach is built on three core pillars that guide everything we do: community-centered design, 
                collaborative partnerships, and innovation. These principles ensure we create sustainable, meaningful impact.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {approaches.map((approach, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card text-center"
                  style={{
                    minHeight: '240px'
                  }}
                >
                  <div className="flex justify-center mb-6">
                    {approach.icon}
                  </div>
                  <h3 
                    className="text-xl font-bold mb-3"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937',
                      lineHeight: 1.3
                    }}
                  >
                    {approach.title}
                  </h3>
                  <p 
                    className="text-gray-600 flex-grow mb-4"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.6
                    }}
                  >
                    {approach.description}
                  </p>
                  <div className="w-full mt-4">
                    <img
                      src={approach.image}
                      alt={approach.title}
                      className="w-full rounded-md card-image"
                      style={{
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        objectFit: 'cover',
                        width: '100%',
                        height: '180px',
                        display: 'block'
                      }}
                    />
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Our Core Principles
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                These fundamental values guide our decision-making and shape our organizational culture.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {principles.map((principle, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card"
                  style={{
                    minHeight: '180px'
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div 
                      className="flex-shrink-0 mt-1"
                      style={{
                        width: '24px',
                        height: '24px'
                      }}
                    >
                      <FaCheckCircle 
                        className="text-primary-600 text-xl" 
                        style={{
                          width: '24px',
                          height: '24px'
                        }}
                      />
                    </div>
                    <div className="flex-grow">
                      <h3 
                        className="text-lg font-bold mb-3"
                        style={{
                          fontFamily: "'Open Sans', system-ui, sans-serif",
                          fontWeight: 700,
                          color: '#1F2937',
                          lineHeight: 1.3
                        }}
                      >
                        {principle.title}
                      </h3>
                      <p 
                        className="text-gray-600"
                        style={{
                          fontFamily: "'Open Sans', system-ui, sans-serif",
                          fontWeight: 400,
                          color: '#374151',
                          lineHeight: 1.6
                        }}
                      >
                        {principle.description}
                      </p>
                    </div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Join Us in Making a Difference
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Be part of our mission to create lasting positive change in our community.
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Apply Now
              <FaArrowRight />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
